import React from 'react';
import { formatMessage } from 'devextreme/localization';

import PathCheckResultDto from '../../classes/dtos/pathmodule/PathCheckResultDto';
import { MessageFormHandle, WarningMessage, ErrorMessage } from '../../components/message-form/MessageForm';
import PathPublishChecklist from '../../components/path-publish-checklist/PathPublishChecklist';
import { PathChecklistItem } from './PathBeforeSaveValidation';

/** Shape shared by every *Update*DetailAsync result. */
interface PersistResult {
    IsFailed(): boolean;
    /** Result payload; `IsPublished` is decided by the back (a save may not create a new version). */
    Result?: { Id?: number, IsPublished: boolean } | null;
}

export interface PerformPathSaveConfig {
    /** The path editor's MessageForm handle; `null` only before it has mounted. */
    messageForm: MessageFormHandle | null;

    /** Result of the shared pre-save validation (see PathBeforeSaveValidation). */
    checkResult: PathCheckResultDto;

    /**
     * Full publish-readiness checklist for the same path (see `getPathPublishChecklist`). When the
     * save is blocked it is shown in the MessageForm as the detailed report; falls back to the plain
     * `checkResult.Message` string when omitted.
     */
    checklist?: PathChecklistItem[];

    /** Whether the user asked to publish, as opposed to saving a draft. */
    publish: boolean;

    /** Fires the module's *Update*DetailAsync call. */
    persist: () => Promise<PersistResult>;

    /**
     * Runs ONLY after the API has confirmed the save: flush the mapping grid, clear the
     * "unsaved changes" flag, show the success toast, module-specific side effects, ...
     * `isPublished` is the back's answer: whether the saved version is the published one.
     * `versionId` is the id of the version the save landed on, when the module's result carries it.
     */
    onSuccess: (isPublished: boolean, versionId?: number) => void;
}

/**
 * Shows the blocking issues that stop a save / publish. Prefers the detailed checklist popup
 * (every check, passed or not) and falls back to the single-line message when no checklist is given.
 */
function openBlockingReport(
    messageForm: MessageFormHandle,
    checklist: PathChecklistItem[] | undefined,
    fallbackMessage: string
): void {
    if (checklist && checklist.length > 0) {
        messageForm.OpenComplex(<PathPublishChecklist Items={checklist} />, WarningMessage, { fitContent: true });
        return;
    }
    messageForm.Open(fallbackMessage, WarningMessage);
}

/**
 * The save / publish flow shared by the five path editors (MetaApi, Reporting, AI, DataTable,
 * Synchro). Centralises the ordering and the surfaces so they can't drift:
 *
 *  - validation blocks  -> MessageForm popup (warning, detailed checklist), no persistence;
 *  - technical failure   -> MessageForm popup (error);
 *  - success             -> the caller's `onSuccess` (toast, grid flush, dirty-flag reset).
 *
 * Unlike the old per-page code it awaits the API, so the success toast can no longer appear
 * for a call that actually failed. Returns `true` when the path was persisted.
 */
export async function performPathSave(config: PerformPathSaveConfig): Promise<boolean> {
    const { messageForm, checkResult, checklist, publish, persist, onSuccess } = config;

    if (messageForm === null) {
        return false;
    }

    if (checkResult.Level === PathCheckResultDto.BlockingForSave) {
        openBlockingReport(messageForm, checklist, formatMessage('PathValidation_ImpossibleToSave', checkResult.Message));
        return false;
    }

    if (publish && checkResult.Level === PathCheckResultDto.BlockingForPublish) {
        openBlockingReport(messageForm, checklist, formatMessage('PathValidation_ImpossibleToSaveAndPublish', checkResult.Message));
        return false;
    }

    let result: PersistResult;
    try {
        result = await persist();
    } catch {
        messageForm.Open(formatMessage('PathValidation_SaveFailed'), ErrorMessage);
        return false;
    }

    if (result.IsFailed()) {
        messageForm.Open(formatMessage('PathValidation_SaveFailed'), ErrorMessage);
        return false;
    }

    onSuccess(result.Result?.IsPublished ?? publish, result.Result?.Id);
    return true;
}
